import * as classnames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";
import { NavLink } from "react-router-dom";
import SyntaxHighlighter from "react-syntax-highlighter";
import { hybrid } from "react-syntax-highlighter/dist/esm/styles/hljs";

// import { Test } from "@components/user/editor/partials/editorAppealComment/Test";
import { Button, Select, PageTitle, Msg } from "@shared/components";
import {
  ApplicantExamModel,
  ApplicantChallengeContextModel,
} from "@shared/models";
import {
  ChallengeStyle,
  ApplicantExamStatus,
  ProgrammingLanguage,
} from "@shared/services/enums";
import { FileUploadConfig } from "@shared/services/fileUpload";
import History from "@shared/services/history";

import { EditorAppealForm } from "../editorAppealForm/EditorAppealForm";
import EditorFileUploader from "../editorFileUploader/EditorFileUploader";
import { SubmitExamAlert } from "../submitExamAlert/SubmitExamAlert";

export interface CodeFile {
  name: string;
  code: string;
}

/**
 * Prop interface
 */
export interface ExternalProps {
  files: Array<CodeFile>;
  exam: ApplicantExamModel;
  challengeId: number;
  challengeContext: ApplicantChallengeContextModel;
  getChallengeContext: (params: {}) => void;
  removeChallengeContext: () => void;
  getPresigned: (payload: {}, params: {}) => void;
  submitExam: () => void;
}

export interface InjectedProps {
  applicantIncludeAppealCommentLogAction: (payload?: {}) => void;
  updateAppealComment: (payload: {}, params: {}) => void;
  resetAppealCommentState: () => void;
  updateFileUploadingState: (isUploading: boolean) => void;
  isUpdatingAppealComment: boolean;
  isUpdateAppealCommentSuccess: boolean;
  isUpdateAppealCommentError: boolean;
  isIncludedAppealCommentLog: boolean;
  isFileUploading: boolean;
  fileUploadConfig: FileUploadConfig;
}

type EditorAppealCommentProps = ExternalProps & InjectedProps;

/**
 * State interface
 */
export interface EditorAppealCommentState {
  selectedFile?: CodeFile;
  tabs: Array<CodeFile>;
  files: Array<CodeFile>;

  canScrollLeft: boolean;
  canScrollRight: boolean;

  appealComment: string;
}

const sortFiles = (a: CodeFile, b: CodeFile) => {
  const nameA = a.name.toLowerCase();
  const nameB = b.name.toLowerCase();

  if (nameA.includes("/") && !nameB.includes("/")) {
    return -1;
  } else if (!nameA.includes("/") && nameB.includes("/")) {
    return 1;
  } else {
    return nameA < nameB ? -1 : nameB < nameA ? 1 : 0;
  }
};

/**
 * Page component
 */
class EditorAppealComment extends React.Component<
  EditorAppealCommentProps,
  EditorAppealCommentState
> {
  private tabsOuter: HTMLElement | null;
  private tabsInner: HTMLElement | null;
  private selectedTab: HTMLElement | null;
  private timeout: number | null;

  constructor(props: EditorAppealCommentProps) {
    super(props);

    // `componentWillRecieveProps` cannot determine to set state or not for AI challenge
    // because it does not have any files or properties to compare.
    // setting appealComment's default value on initialize.
    const relatedResult = props.exam.getResultByChallengeId(props.challengeId);

    this.state = {
      selectedFile: undefined,
      tabs: [],
      files: [],
      canScrollLeft: false,
      canScrollRight: false,
      appealComment: relatedResult.appealComment || "",
    };
  }

  public componentDidMount(): void {
    this.props.resetAppealCommentState();
    this.props.applicantIncludeAppealCommentLogAction();
  }

  public UNSAFE_componentWillMount() {
    // redirect if exam finished
    if (
      this.props.exam &&
      this.props.exam.status !== ApplicantExamStatus.InProgress
    ) {
      History.push(
        `/${this.props.exam.org.name}/exams/${this.props.exam.urlToken}/challenges`,
      );
      return;
    }

    // get files
    this.props.getChallengeContext(
      this.props.exam.getResultByChallengeId(this.props.challengeId).id,
    );
  }

  public UNSAFE_componentWillReceiveProps(nextProps: EditorAppealCommentProps) {
    const nextResult = nextProps.exam.getResultByChallengeId(
      nextProps.challengeId,
    );
    const nextContext = nextProps.challengeContext;
    if (
      !isEqual(this.props.challengeContext, nextContext) &&
      nextContext.answers &&
      nextContext.answers.fileVersions
    ) {
      nextProps.getPresigned(
        { files: Object.keys(nextContext.answers.fileVersions) },
        nextResult.id,
      );

      return this.setState({
        selectedFile: nextProps.files.length ? nextProps.files[0] : undefined,
        tabs: nextProps.files.length ? [nextProps.files[0]] : [],
        canScrollLeft: false,
        canScrollRight: false,
        appealComment: nextResult.appealComment || "",
        files: nextProps.files.sort(sortFiles),
      });
    }

    if (!isEqual(this.props.files, nextProps.files)) {
      return this.setState({
        selectedFile: nextProps.files.length ? nextProps.files[0] : undefined,
        tabs: nextProps.files.length ? [nextProps.files[0]] : [],
        files: nextProps.files.sort(sortFiles),
      });
    }
  }

  public componentDidUpdate(prevProps: EditorAppealCommentProps) {
    if (
      !isEqual(this.props.files, prevProps.files) &&
      this.props.challengeContext.answers
    ) {
      this.selectFile(this.props.challengeContext.answers.mainFile);
    }
  }

  public shouldComponentUpdate(
    nextProps: EditorAppealCommentProps,
    nextState: EditorAppealCommentState,
  ) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  public componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.props.removeChallengeContext();
  }

  public render() {
    const {
      exam,
      challengeId,
      isUpdateAppealCommentError,
      isUpdateAppealCommentSuccess,
      isUpdatingAppealComment,
      isFileUploading,
      updateFileUploadingState,
    } = this.props;
    const { files } = this.state;

    const file = this.state.selectedFile;
    const challenge = exam.getChallengeByChallengeId(challengeId);

    const rootStyle = classnames("code-editor-appeal-comment", {
      [`is-${(ChallengeStyle[challenge.style] || "").toLowerCase()}`]:
        challenge.style !== undefined,
    });

    const tabs = this.state.tabs.map((tab, index) => (
      <li
        key={tab.name}
        title={tab.name}
        className={`file-tab ${
          tab.name ===
          (this.state.selectedFile ? this.state.selectedFile.name : "")
            ? " selected-tab"
            : ""
        }`}
        onClick={() => this.selectFile(tab.name)}
        ref={(ref) => {
          if (
            tab.name ===
            (this.state.selectedFile ? this.state.selectedFile.name : "")
          ) {
            this.selectedTab = ref;
          }
        }}
      >
        <span className="tab-label">{tab.name}</span>
        <span
          className="tab-close"
          onClick={(e) => {
            e.stopPropagation();
            this.removeTab(tab.name);
          }}
        >
          ×
        </span>
      </li>
    ));

    return (
      <div className={rootStyle}>
        {exam.areAllChallengesFinished() && (
          <SubmitExamAlert
            className="code-editor-appeal-comment__alert"
            submitExam={this.props.submitExam}
            showCloseButton
            disabled={isFileUploading}
          />
        )}

        <div className="code-editor-appeal-comment__content">
          <div className="code-editor-appeal-comment__codeblock">
            <div className="code-editor-appeal-comment__title">
              <PageTitle>
                <Msg id="appeal.title" />
                <small style={{ fontSize: "0.75rem" }}>
                  <Msg id="appeal.subtitle" />
                </small>
              </PageTitle>
              {exam.isChallengeStarted(challengeId) && (
                <NavLink
                  className="code-editor-appeal-comment__editor-link"
                  to={`/${exam.org.name}/exams/${exam.urlToken}/challenges/${challengeId}`}
                >
                  <Button size="small">
                    <Msg id="applicant-challenge-link.updateAnswer" />
                  </Button>
                </NavLink>
              )}
            </div>

            <div className="code-editor-appeal-comment__codeblock-header">
              <div
                className={classnames("tab-scroll", {
                  disabled: !this.state.canScrollLeft,
                })}
                onClick={() => this.state.canScrollLeft && this.scrollTabs()}
              >
                &lt;
              </div>
              <div
                ref={(ref) => (this.tabsOuter = ref)}
                className="code-editor-appeal-comment__tabs"
              >
                <ul ref={(ref) => (this.tabsInner = ref)} className="tab-list">
                  {tabs}
                </ul>
                {this.state.canScrollLeft && <div className="gradient-left" />}
                {this.state.canScrollRight && (
                  <div className="gradient-right" />
                )}
              </div>
              <div
                className={classnames("tab-scroll", {
                  disabled: !this.state.canScrollRight,
                })}
                onClick={() =>
                  this.state.canScrollRight && this.scrollTabs(true)
                }
              >
                &gt;
              </div>
              <Select
                className="file-select"
                value={file ? file.name : ""}
                options={files.map((codefile) => ({
                  value: codefile.name,
                  label: codefile.name,
                }))}
                onChange={(e: React.FormEvent<HTMLSelectElement>) =>
                  this.selectFile((e.target as HTMLSelectElement).value)
                }
              />
            </div>
            {file && (
              <SyntaxHighlighter
                className="code-editor-appeal-comment__codeblock-code"
                style={hybrid}
                language={ProgrammingLanguage.getLanguage(file.name)}
                showLineNumbers={true}
              >
                {file.code}
              </SyntaxHighlighter>
            )}
          </div>
          <div
            className={classnames(
              "code-editor-appeal-comment__comment",
              "control",
              {
                "code-editor-appeal-comment__comment__ai":
                  challenge.style === ChallengeStyle.AI,
              },
            )}
          >
            <Button
              className="code-editor-appeal-comment__comment__button"
              route={{
                pathname: `/${exam.org.name}/exams/${exam.urlToken}/challenges`,
              }}
              pageReload={ChallengeStyle.shouldUseVsCodeEditor(challenge.style)}
              ariaLabel="Back To Challenge List"
            >
              <Msg id="button.backToChallengeList" />
            </Button>
            <div className="code-editor-appeal-comment__comment__content">
              <EditorAppealForm
                descriptionKey="appeal.yourComment.description"
                isUpdating={isUpdatingAppealComment}
                isUpdateFail={isUpdateAppealCommentError}
                isUpdateSuccess={isUpdateAppealCommentSuccess}
                value={this.state.appealComment}
                titleClass="code-editor-appeal-comment__comment__title"
                onChange={(value) => {
                  this.setState({ appealComment: value });
                  this.onUpdateAppealComment();
                }}
              />
            </div>
            {this.props.fileUploadConfig?.uploadEnabled && (
              <EditorFileUploader
                challengeResultId={
                  this.props.challengeContext.challengeResultId
                }
                fileUploadConfig={this.props.fileUploadConfig}
                updateFileUploadingState={updateFileUploadingState}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  private selectFile = (name: string) => {
    const selectedFile = this.props.files.find((f) => f.name === name);

    if (!selectedFile) {
      return;
    }

    const tabs = [...this.state.tabs];

    if (!tabs.find((t) => t.name === name)) {
      tabs.push(selectedFile);
    }

    this.setState(
      {
        tabs,
        selectedFile,
      },
      this.scrollToSelected,
    );
  };

  private removeTab = (name: string) => {
    if (this.state.tabs.length > 1) {
      let selectedFile = this.state.selectedFile;

      const tabs = this.state.tabs.filter((tab) => tab.name !== name);
      if (selectedFile && selectedFile.name === name) {
        selectedFile = tabs[0];
      }
      this.setState(
        {
          selectedFile,
          tabs,
        },
        this.scrollToSelected,
      );
    }
  };

  private scrollToSelected = () => {
    const element = this.selectedTab;

    if (element && this.tabsInner && this.tabsOuter) {
      const leftOffset = element.offsetLeft + this.tabsInner.offsetLeft;
      const rightOffset =
        element.offsetLeft + element.offsetWidth + this.tabsInner.offsetLeft;

      if (leftOffset < 75) {
        this.scrollTabs(false, 75 - leftOffset);
      } else if (rightOffset > this.tabsOuter.offsetWidth - 75) {
        this.scrollTabs(true, rightOffset - (this.tabsOuter.offsetWidth - 75));
      }
    }
  };

  private scrollTabs = (right?: boolean, by?: number) => {
    if (this.tabsInner && this.tabsOuter) {
      const max = 0;
      const min = this.tabsOuter.offsetWidth - this.tabsInner.offsetWidth;

      const amount = by || 100;

      this.tabsInner.style.left =
        Math.min(
          max,
          Math.max(min, this.tabsInner.offsetLeft - (right ? amount : -amount)),
        ) + "px";
      setTimeout(this.checkTabPosition, 200);
    }
  };

  private checkTabPosition = () => {
    if (!this.tabsOuter || !this.tabsInner) {
      return;
    }

    this.setState({
      canScrollLeft: this.tabsInner.offsetLeft < 0,
      canScrollRight:
        this.tabsInner.offsetWidth > this.tabsOuter.offsetWidth &&
        this.tabsInner.offsetLeft >
          this.tabsOuter.offsetWidth - this.tabsInner.offsetWidth,
    });
  };

  private onUpdateAppealComment = () => {
    this.props.updateAppealComment(
      { appealComment: this.state.appealComment },
      this.props.exam.getResultByChallengeId(this.props.challengeId).id,
    );
  };
}

export default EditorAppealComment;
